import React, { useEffect, useRef, useState } from 'react';
import { Tooltip } from '@mui/material';
import { useStyles } from './Tooltip.styles';
import TextRender from '../TextRender/TextRender';
import ClickAwayListener from 'react-click-away-listener';
import { trackEvent } from 'helpers/Analytics';

interface TooltipProps {
  information: string;
  title?: string;
  white?: boolean;
  isBlurSafe?: boolean;
  supportInLink?: boolean;
  ariaLabelDisclaimer?: string;
  trigger: string;
  children?: JSX.Element | JSX.Element[] | string;
  trackData?: {
    label: string;
    section: string;
  };
  placement?: 'top' | 'bottom' | 'left' | 'right';
}
const TooltipDisclaimer = (data: TooltipProps): JSX.Element => {
  const ref: any = useRef();
  const refTooltip: any = useRef();
  const [open, setOpen] = useState(false);
  const [blurSafe, setBlurSafe] = useState(false);
  const classes = useStyles({ isLight: !!data.white });
  const dataLength = data.information.length;
  const tooltipClasses = {
    tooltip: `${classes.tooltip} ${
      dataLength > 700 ? 'large' : dataLength > 360 ? 'medium' : ''
    }`,
    tooltipArrow: classes.arrow,
  };

  useEffect(() => {
    if (open) {
      window.addEventListener('scroll', handleTooltipClose, true);
    } else {
      window.removeEventListener('scroll', handleTooltipClose);
    }
    return () => {
      window.removeEventListener('scroll', handleTooltipClose);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleTooltipClose = () => {
    if (open) {
      if (!blurSafe) {
        setTimeout(() => setOpen(false), 300);
        ref.current
          ?.querySelector('a')
          ?.removeEventListener('keydown', handleTabNavigation);
      } else {
        setBlurSafe(false);
      }
    } else if (!blurSafe && ref.current && ref.current.querySelector('a')) {
      setTimeout(refTooltip.current!.focus(), 300);
    }
  };

  const handleTooltipOpen = (event: React.SyntheticEvent) => {
    event.preventDefault();
    data.supportInLink && event.stopPropagation();
    if (data.isBlurSafe) {
      setBlurSafe(true);
    }
    if (data.trackData) {
      trackEvent({
        label: `${data.trackData.section}:${data.trackData.label}:info icon`,
        category: {
          text: data.trackData.label,
          includePageName: true,
        },
        type: 'modal',
      });
    }
    setOpen(true);
    setTimeout(() => focusTooltip(), 300);
  };

  const focusTooltip = () => {
    // TODO rethink how to handle the focus links resolving issues from WTSJOAHM-1489
    // if (ref.current.querySelector('a')) {
    //   ref.current.querySelector('a').focus();
    //   document.body.classList.remove('acr-focus-disabled');
    //   ref.current
    //     .querySelector('a')
    //     .addEventListener('keydown', handleTabNavigation);
    // }
  };

  const handleTabNavigation = (e: { key: string }) => {
    if (e.key === 'Tab') {
      handleTooltipClose();
    }
  };
  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <Tooltip
        onClose={handleTooltipClose}
        classes={tooltipClasses}
        open={open}
        data-test-id="tooltip"
        disableFocusListener
        disableHoverListener
        disableTouchListener
        arrow
        placement={data.placement || 'top'}
        leaveDelay={300}
        ref={refTooltip}
        title={
          <>
            {data.title && (
              <p
                data-testid="tooltipTitle"
                className={`acr-caption-10 ${classes.tooltip_title}`}
              >
                {data.title}
              </p>
            )}
            <p
              ref={ref}
              data-testid="tooltipContent"
              className={`acr-paragraph-9 ${classes.tooltip_content} `}
              tabIndex={1}
            >
              <TextRender className="content" text={data.information} />
            </p>
          </>
        }
      >
        {data.children ? (
          <div
            onClick={handleTooltipOpen}
            className={classes.children_btn}
            onKeyPress={(e) => e.key === 'Enter' && handleTooltipOpen(e)}
            tabIndex={0}
            aria-label={data.ariaLabelDisclaimer || 'Open Disclaimer'}
          >
            {data.children}
          </div>
        ) : (
          <sup
            data-testid="tooltipTrigger"
            onClick={handleTooltipOpen}
            onKeyPress={(e) => e.key === 'Enter' && handleTooltipOpen(e)}
            className={`${classes.trigger_button} ${classes.trigger_sup} ${
              data.white ? 'is-light' : 'is-dark'
            }`}
            tabIndex={0}
            aria-label={data.ariaLabelDisclaimer || 'Open Disclaimer'}
          >
            {!isNaN(data.trigger as any) ? (
              <sup>{data.trigger}</sup>
            ) : (
              data.trigger
            )}
          </sup>
        )}
      </Tooltip>
    </ClickAwayListener>
  );
};
export default TooltipDisclaimer;
